import {
  ZuruflexApiError,
  type ProviderRoute,
  type ProvidersHealth,
  type ProviderSummary,
  type ProviderTestResult,
} from '@zuruflex/api-client';
import { formatDateTimeIn, toLocale } from '@zuruflex/i18n';
import {
  isRouteCountryCode,
  isRouteCurrency,
  PROVIDER_ROUTE_KINDS,
  PROVIDER_ROUTE_NOTES_MAX,
  PROVIDER_ROUTE_PRIORITY_MAX,
  PROVIDER_ROUTE_PRIORITY_MIN,
  type ProviderRouteKind,
  type ProviderTestKind,
  type UpdateProviderRouteInput,
} from '@zuruflex/types';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  FormField,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@zuruflex/ui';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { ChevronLeft, Loader2, Pencil, Plus, RefreshCw, Send, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

const OVERVIEW_KEY = ['providers'] as const;
/*
 * Not under `providers`: every write invalidates that prefix, and health is
 * one network call per vendor — re-probing Twilio because somebody flipped
 * a switch would be slow and tell nobody anything new.
 */
const HEALTH_KEY = ['provider-health'] as const;

const is409 = (error: unknown): error is ZuruflexApiError =>
  error instanceof ZuruflexApiError && error.status === 409;

const errorText = (error: unknown, fallback: string): string =>
  error instanceof ZuruflexApiError ? error.message : fallback;

/**
 * A 409 on a rule means one of two things, and the API words both as
 * CONFLICT: somebody saved the row first (stale), or the edit would give a
 * provider a second rule for the same scope (duplicate). Rereading the
 * table tells them apart — if the row still carries the version the edit
 * started from, nobody else touched it, so it was the duplicate.
 */
async function isStale(
  queryClient: QueryClient,
  id: string,
  baseVersion: number,
): Promise<boolean> {
  const fresh = await api.listProviders();
  queryClient.setQueryData(OVERVIEW_KEY, fresh);
  void queryClient.invalidateQueries({ queryKey: OVERVIEW_KEY });
  const row = fresh.routes.find((route) => route.id === id);
  return !row || row.version !== baseVersion;
}

/**
 * Which email, SMS and payment adapter handles a request, for whom, in
 * what order — the `provider_routes` table, with the adapters it ranks.
 *
 * Laid out like the flags screen, because it works like flags: versioned
 * rows, a switch any manager may flip to pull a failing vendor out of
 * rotation, and everything else an owner's call. Health loads on its own,
 * after the page, because it is one real network call per vendor.
 */
export function ProvidersPage() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // The read endpoints are MANAGER+ too, so an agent gets the note and no requests.
  const canToggle = user?.role === 'OWNER' || user?.role === 'MANAGER';
  const canEdit = user?.role === 'OWNER';

  const [creating, setCreating] = useState<ProviderRouteKind | null>(null);
  const [editing, setEditing] = useState<{ id: string; revision: number } | null>(null);
  const [deleting, setDeleting] = useState<ProviderRoute | null>(null);
  const [testing, setTesting] = useState<ProviderSummary | null>(null);

  const overview = useQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => api.listProviders(),
    enabled: canToggle,
  });

  const health = useQuery({
    queryKey: HEALTH_KEY,
    queryFn: () => api.providerHealth(),
    enabled: canToggle,
    staleTime: 60_000,
    retry: false,
  });

  const toggle = useMutation({
    mutationFn: (input: { route: ProviderRoute; enabled: boolean }) =>
      api.toggleProviderRoute(input.route.id, input.enabled, input.route.version),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: OVERVIEW_KEY }),
  });

  if (!canToggle) {
    return (
      <div className="space-y-6">
        <Header />
        <Alert variant="info">{t('providers.readOnlyAgent')}</Alert>
      </div>
    );
  }

  if (overview.error) {
    return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;
  }

  const data = overview.data;
  const providers = data?.providers ?? [];
  const nameOf = (id: string, kind: ProviderRouteKind) =>
    providers.find((p) => p.id === id && p.kind === kind)?.displayName ?? id;
  const editingRoute = editing ? data?.routes.find((route) => route.id === editing.id) : undefined;

  return (
    <div className="space-y-6">
      <Header
        action={
          <Button
            variant="outline"
            disabled={health.isFetching}
            onClick={() => void health.refetch()}
          >
            <RefreshCw className={health.isFetching ? 'animate-spin' : undefined} />
            {t('providers.health.recheck')}
          </Button>
        }
      />

      {!canEdit ? <Alert variant="info">{t('providers.managerNote')}</Alert> : null}

      {toggle.error ? (
        <Alert variant={is409(toggle.error) ? 'warning' : 'danger'}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{errorText(toggle.error, t('common:error.unexpected'))}</span>
            <Button size="sm" variant="outline" onClick={() => toggle.reset()}>
              {t('common:action.close')}
            </Button>
          </div>
        </Alert>
      ) : null}

      {health.error ? (
        <Alert variant="warning">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{t('providers.health.failed')}</span>
            <Button size="sm" variant="outline" onClick={() => void health.refetch()}>
              {t('providers.health.recheck')}
            </Button>
          </div>
        </Alert>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_400px]">
        <div className="min-w-0 space-y-6">
          {!data
            ? PROVIDER_ROUTE_KINDS.map((kind) => <Skeleton key={kind} className="h-72 w-full" />)
            : PROVIDER_ROUTE_KINDS.map((kind) => (
                <KindSection
                  key={kind}
                  kind={kind}
                  providers={providers.filter((p) => p.kind === kind)}
                  routes={data.routes.filter((route) => route.kind === kind)}
                  health={health.data}
                  healthPending={health.isPending && !health.error}
                  canToggle={canToggle}
                  canEdit={canEdit}
                  locale={locale}
                  toggleDisabled={toggle.isPending}
                  nameOf={(id) => nameOf(id, kind)}
                  onToggle={(route, enabled) => toggle.mutate({ route, enabled })}
                  onAdd={() => setCreating(kind)}
                  onEdit={(route) => setEditing({ id: route.id, revision: 0 })}
                  onDelete={setDeleting}
                  onTest={setTesting}
                />
              ))}
        </div>

        <div className="xl:sticky xl:top-6 xl:self-start">
          <ResolutionPreview
            unscoped={data?.resolution}
            nameOf={nameOf}
          />
        </div>
      </div>

      {creating && data ? (
        <RuleDialog
          kind={creating}
          providers={providers.filter((p) => p.kind === creating)}
          onClose={() => setCreating(null)}
        />
      ) : null}

      {editing && editingRoute ? (
        <RuleDialog
          key={`${editingRoute.id}-${editing.revision}`}
          kind={editingRoute.kind}
          providers={providers.filter((p) => p.kind === editingRoute.kind)}
          route={editingRoute}
          onReload={() => setEditing({ id: editing.id, revision: editing.revision + 1 })}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {deleting ? (
        <DeleteDialog
          route={deleting}
          providerName={nameOf(deleting.providerId, deleting.kind)}
          onClose={() => setDeleting(null)}
        />
      ) : null}

      {testing ? <TestDialog provider={testing} onClose={() => setTesting(null)} /> : null}
    </div>
  );
}

function Header({ action }: { action?: ReactNode }) {
  const { t } = useTranslation(['admin', 'common']);
  return (
    <PageHeader
      eyebrow={
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 text-sm text-text-muted transition-colors hover:text-text"
        >
          <ChevronLeft className="size-4" />
          {t('providers.back')}
        </Link>
      }
      title={t('providers.title')}
      description={t('providers.subtitle')}
      action={action}
    />
  );
}

// ── One kind: its adapters and its rules ─────────────────────────────────

function KindSection({
  kind,
  providers,
  routes,
  health,
  healthPending,
  canToggle,
  canEdit,
  locale,
  toggleDisabled,
  nameOf,
  onToggle,
  onAdd,
  onEdit,
  onDelete,
  onTest,
}: {
  kind: ProviderRouteKind;
  providers: ProviderSummary[];
  routes: ProviderRoute[];
  health: ProvidersHealth | undefined;
  healthPending: boolean;
  canToggle: boolean;
  canEdit: boolean;
  locale: ReturnType<typeof toLocale>;
  toggleDisabled: boolean;
  nameOf: (id: string) => string;
  onToggle: (route: ProviderRoute, enabled: boolean) => void;
  onAdd: () => void;
  onEdit: (route: ProviderRoute) => void;
  onDelete: (route: ProviderRoute) => void;
  onTest: (provider: ProviderSummary) => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const any = t('providers.rules.any');

  return (
    <Card>
      <CardHeader className="flex-wrap">
        <div>
          <CardTitle>{t(`providers.kind.${kind}.title`)}</CardTitle>
          <CardDescription>{t(`providers.kind.${kind}.body`)}</CardDescription>
        </div>
        {canEdit ? (
          <Button variant="outline" onClick={onAdd}>
            <Plus />
            {t('providers.rules.add')}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-4">
          {providers.map((provider) => (
            <AdapterTile
              key={provider.id}
              provider={provider}
              health={health?.[`${provider.kind}:${provider.id}`]}
              healthPending={healthPending}
              canTest={canEdit && provider.testable && provider.isConfigured}
              onTest={() => onTest(provider)}
            />
          ))}
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-text">{t('providers.rules.title')}</h3>
          {routes.length === 0 ? (
            <div className="rounded-card border border-dashed border-border-default p-6 text-center">
              <p className="text-sm font-semibold text-text">{t('providers.rules.emptyTitle')}</p>
              <p className="mx-auto mt-1 max-w-2xl text-sm text-text-muted">
                {t('providers.rules.emptyBody')}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('providers.rules.column.provider')}</TableHead>
                  <TableHead>{t('providers.rules.column.country')}</TableHead>
                  <TableHead>{t('providers.rules.column.currency')}</TableHead>
                  <TableHead>{t('providers.rules.column.priority')}</TableHead>
                  <TableHead>{t('providers.rules.column.enabled')}</TableHead>
                  <TableHead>{t('providers.rules.column.notes')}</TableHead>
                  <TableHead>{t('providers.rules.column.updated')}</TableHead>
                  {canEdit ? (
                    <TableHead className="text-right">
                      <span className="sr-only">{t('providers.rules.column.actions')}</span>
                    </TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {routes.map((route) => (
                  <TableRow key={route.id}>
                    <TableCell>
                      <p className="font-medium">{nameOf(route.providerId)}</p>
                      <p className="font-mono text-xs text-text-muted">{route.providerId}</p>
                    </TableCell>
                    <TableCell>
                      {route.countryCode ? (
                        <Badge variant="soft" className="font-mono">
                          {route.countryCode}
                        </Badge>
                      ) : (
                        <Badge variant="outline">{any}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {route.currency ? (
                        <Badge variant="soft" className="font-mono">
                          {route.currency}
                        </Badge>
                      ) : (
                        <Badge variant="outline">{any}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums">{route.priority}</TableCell>
                    <TableCell>
                      <Switch
                        checked={route.isEnabled}
                        disabled={!canToggle || toggleDisabled}
                        aria-label={t('providers.rules.toggleLabel', {
                          provider: nameOf(route.providerId),
                        })}
                        onCheckedChange={(enabled) => onToggle(route, enabled)}
                      />
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <p className="line-clamp-2 text-sm text-text-muted" title={route.notes ?? ''}>
                        {route.notes || '—'}
                      </p>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-text-muted">
                      {formatDateTimeIn(route.updatedAt, locale)}
                    </TableCell>
                    {canEdit ? (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            aria-label={t('providers.rules.edit')}
                            onClick={() => onEdit(route)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            aria-label={t('providers.rules.delete')}
                            onClick={() => onDelete(route)}
                          >
                            <Trash2 />
                          </Button>
                        </div>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function AdapterTile({
  provider,
  health,
  healthPending,
  canTest,
  onTest,
}: {
  provider: ProviderSummary;
  health: ProvidersHealth[string] | undefined;
  healthPending: boolean;
  canTest: boolean;
  onTest: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);

  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-card border border-border-default p-4">
      <div className="min-w-0">
        <p className="truncate font-medium text-text">{provider.displayName}</p>
        <p className="font-mono text-xs text-text-muted">{provider.id}</p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {provider.isConfigured ? (
          <Badge variant="success">{t('providers.adapter.configured')}</Badge>
        ) : (
          <Badge variant="default" title={t('providers.adapter.notConfiguredHint')}>
            {t('providers.adapter.notConfigured')}
          </Badge>
        )}
        {/* An adapter with no credentials is not "failing"; it was never asked. */}
        {provider.isConfigured ? (
          <HealthBadge health={health} pending={healthPending} />
        ) : null}
      </div>
      {provider.isConfigured && health && !health.ok && health.detail ? (
        <p className="break-words text-xs text-danger">{health.detail}</p>
      ) : null}
      {!provider.isConfigured ? (
        <p className="text-xs text-text-muted">{t('providers.adapter.notConfiguredHint')}</p>
      ) : null}
      {canTest ? (
        <div className="mt-auto">
          <Button size="sm" variant="outline" onClick={onTest}>
            <Send />
            {t('providers.test.action')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function HealthBadge({
  health,
  pending,
}: {
  health: ProvidersHealth[string] | undefined;
  pending: boolean;
}) {
  const { t } = useTranslation(['admin', 'common']);
  if (pending) {
    return (
      <Badge variant="outline" className="gap-1">
        <Loader2 className="size-3 animate-spin" />
        {t('providers.health.checking')}
      </Badge>
    );
  }
  if (!health) return <Badge variant="outline">{t('providers.health.unknown')}</Badge>;
  if (!health.ok) return <Badge variant="danger">{t('providers.health.failing')}</Badge>;
  return (
    <Badge variant="info">
      {health.latencyMs !== undefined
        ? t('providers.health.ok', { ms: health.latencyMs })
        : t('providers.health.okNoLatency')}
    </Badge>
  );
}

// ── Resolution preview ───────────────────────────────────────────────────

/** Upper case, letters only, at most `max` — what the table stores. */
const code = (raw: string, max: number) =>
  raw
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, max);

function ResolutionPreview({
  unscoped,
  nameOf,
}: {
  unscoped: Record<ProviderRouteKind, string[]> | undefined;
  nameOf: (id: string, kind: ProviderRouteKind) => string;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const [country, setCountry] = useState('');
  const [currency, setCurrency] = useState('');

  // A half-typed code is not a scope yet; keep showing the last one that was.
  const countryReady = country === '' || isRouteCountryCode(country);
  const currencyReady = currency === '' || isRouteCurrency(currency);
  const scoped = country !== '' || currency !== '';

  const preview = useQuery({
    queryKey: [...OVERVIEW_KEY, 'preview', country, currency],
    queryFn: () =>
      api.listProviders({
        ...(country ? { countryCode: country } : {}),
        ...(currency ? { currency } : {}),
      }),
    enabled: scoped && countryReady && currencyReady,
    placeholderData: keepPreviousData,
  });

  const resolution = scoped ? preview.data?.resolution : unscoped;

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>{t('providers.preview.title')}</CardTitle>
          <CardDescription>{t('providers.preview.body')}</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <FormField label={t('providers.preview.country')}>
            {(field) => (
              <Input
                {...field}
                value={country}
                placeholder={t('providers.preview.anyPlaceholder')}
                className="font-mono uppercase placeholder:font-sans placeholder:normal-case"
                onChange={(event) => setCountry(code(event.target.value, 2))}
              />
            )}
          </FormField>
          <FormField label={t('providers.preview.currency')}>
            {(field) => (
              <Input
                {...field}
                value={currency}
                placeholder={t('providers.preview.anyPlaceholder')}
                className="font-mono uppercase placeholder:font-sans placeholder:normal-case"
                onChange={(event) => setCurrency(code(event.target.value, 3))}
              />
            )}
          </FormField>
        </div>

        {preview.error ? (
          <Alert variant="danger">{errorText(preview.error, t('common:error.unexpected'))}</Alert>
        ) : null}

        <div className="space-y-4">
          {PROVIDER_ROUTE_KINDS.map((kind) => {
            const chain = resolution?.[kind];
            return (
              <div key={kind} className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  {t(`providers.kind.${kind}.title`)}
                </p>
                {!chain ? (
                  <Skeleton className="h-8 w-full" />
                ) : chain.length === 0 ? (
                  <p className="text-sm text-danger">{t('providers.preview.none')}</p>
                ) : (
                  <ol className="flex flex-wrap items-center gap-1.5">
                    {chain.map((id, index) => (
                      <li key={id} className="flex items-center gap-1.5">
                        {index > 0 ? <span className="text-text-muted">→</span> : null}
                        <Badge variant={index === 0 ? 'brand' : 'default'}>
                          <span className="tabular-nums">{index + 1}.</span> {nameOf(id, kind)}
                        </Badge>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Add / edit a rule ────────────────────────────────────────────────────

interface RuleForm {
  providerId: string;
  countryCode: string;
  currency: string;
  priority: string;
  isEnabled: boolean;
  notes: string;
}

/**
 * One dialog for both: a new rule (any of the kind's adapters) or an edit
 * of an existing one, whose provider is fixed — the API does not move a
 * rule between vendors, and pretending it could would be a delete and an
 * add wearing a disguise.
 */
function RuleDialog({
  kind,
  providers,
  route,
  onReload,
  onClose,
}: {
  kind: ProviderRouteKind;
  providers: ProviderSummary[];
  route?: ProviderRoute;
  onReload?: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const [form, setForm] = useState<RuleForm>(() => ({
    providerId: route?.providerId ?? providers[0]?.id ?? '',
    countryCode: route?.countryCode ?? '',
    currency: route?.currency ?? '',
    priority: String(route?.priority ?? 100),
    isEnabled: route?.isEnabled ?? true,
    notes: route?.notes ?? '',
  }));
  /** True once a 409 turned out to be somebody else's save, not a duplicate. */
  const [stale, setStale] = useState(false);

  const set = <K extends keyof RuleForm>(field: K, value: RuleForm[K]) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const priority = Number(form.priority);
  const errors = {
    countryCode: isRouteCountryCode(form.countryCode) ? undefined : t('providers.form.badCountry'),
    currency: isRouteCurrency(form.currency) ? undefined : t('providers.form.badCurrency'),
    priority:
      form.priority.trim() !== '' &&
      Number.isInteger(priority) &&
      priority >= PROVIDER_ROUTE_PRIORITY_MIN &&
      priority <= PROVIDER_ROUTE_PRIORITY_MAX
        ? undefined
        : t('providers.form.badPriority', {
            min: PROVIDER_ROUTE_PRIORITY_MIN,
            max: PROVIDER_ROUTE_PRIORITY_MAX,
          }),
    notes:
      form.notes.length <= PROVIDER_ROUTE_NOTES_MAX
        ? undefined
        : t('providers.form.notesTooLong', { max: PROVIDER_ROUTE_NOTES_MAX }),
  };
  const invalid = Object.values(errors).some(Boolean) || !form.providerId;

  /** For an edit, only what changed — a manager-style patch stays a manager-style patch. */
  const patch = (() => {
    if (!route) return null;
    const next: UpdateProviderRouteInput = { expectedVersion: route.version };
    if (priority !== route.priority) next.priority = priority;
    if (form.countryCode !== route.countryCode) next.countryCode = form.countryCode;
    if (form.currency !== route.currency) next.currency = form.currency;
    const notes = form.notes.trim() || null;
    if (notes !== (route.notes || null)) next.notes = notes;
    return next;
  })();
  const unchanged = patch !== null && Object.keys(patch).length === 1;

  const save = useMutation({
    mutationFn: async () => {
      if (!route) {
        return api.createProviderRoute({
          kind,
          providerId: form.providerId,
          priority,
          isEnabled: form.isEnabled,
          countryCode: form.countryCode,
          currency: form.currency,
          notes: form.notes.trim() || null,
        });
      }
      try {
        return await api.updateProviderRoute(route.id, patch ?? { expectedVersion: route.version });
      } catch (error) {
        if (is409(error) && (await isStale(queryClient, route.id, route.version))) setStale(true);
        throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OVERVIEW_KEY });
      onClose();
    },
  });

  const providerName = providers.find((p) => p.id === form.providerId)?.displayName ?? form.providerId;
  const fieldErrors = save.error instanceof ZuruflexApiError ? save.error.fields : undefined;

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {route
              ? t('providers.form.editTitle', { provider: providerName })
              : t('providers.form.createTitle', { kind: t(`providers.kind.${kind}.title`) })}
          </DialogTitle>
          <DialogDescription>{t('providers.form.body')}</DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!invalid && !unchanged) save.mutate();
          }}
        >
          <FormField
            label={t('providers.form.provider')}
            hint={route ? t('providers.form.providerFixed') : undefined}
          >
            {(field) => (
              <Select
                value={form.providerId}
                disabled={Boolean(route)}
                onValueChange={(value) => set('providerId', value)}
              >
                <SelectTrigger id={field.id}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((provider) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      {provider.displayName}
                      {provider.isConfigured ? '' : ` · ${t('providers.adapter.notConfigured')}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField
              label={t('providers.form.country')}
              hint={t('providers.form.countryHint')}
              error={form.countryCode.length === 2 ? errors.countryCode : undefined}
            >
              {(field) => (
                <Input
                  {...field}
                  value={form.countryCode}
                  placeholder={t('providers.rules.any')}
                  className="font-mono uppercase placeholder:font-sans placeholder:normal-case"
                  onChange={(event) => set('countryCode', code(event.target.value, 2))}
                />
              )}
            </FormField>
            <FormField
              label={t('providers.form.currency')}
              hint={t('providers.form.currencyHint')}
              error={form.currency.length === 3 ? errors.currency : undefined}
            >
              {(field) => (
                <Input
                  {...field}
                  value={form.currency}
                  placeholder={t('providers.rules.any')}
                  className="font-mono uppercase placeholder:font-sans placeholder:normal-case"
                  onChange={(event) => set('currency', code(event.target.value, 3))}
                />
              )}
            </FormField>
          </div>

          <div className="grid grid-cols-2 items-start gap-3">
            <FormField
              label={t('providers.form.priority')}
              hint={t('providers.form.priorityHint', {
                min: PROVIDER_ROUTE_PRIORITY_MIN,
                max: PROVIDER_ROUTE_PRIORITY_MAX,
              })}
              error={errors.priority}
            >
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  inputMode="numeric"
                  min={PROVIDER_ROUTE_PRIORITY_MIN}
                  max={PROVIDER_ROUTE_PRIORITY_MAX}
                  step={1}
                  value={form.priority}
                  onChange={(event) => set('priority', event.target.value)}
                />
              )}
            </FormField>
            {/* The switch on an existing rule lives in the table, where a manager can reach it. */}
            {route ? null : (
              <FormField label={t('providers.form.enabled')}>
                {(field) => (
                  <div className="flex h-10 items-center">
                    <Switch
                      id={field.id}
                      checked={form.isEnabled}
                      onCheckedChange={(value) => set('isEnabled', value)}
                    />
                  </div>
                )}
              </FormField>
            )}
          </div>

          <FormField
            label={t('providers.form.notes')}
            hint={t('providers.form.notesHint')}
            error={errors.notes}
          >
            {(field) => (
              <Textarea
                {...field}
                rows={3}
                value={form.notes}
                onChange={(event) => set('notes', event.target.value)}
              />
            )}
          </FormField>

          {save.error ? (
            <Alert variant={stale || is409(save.error) ? 'warning' : 'danger'}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="space-y-1">
                  <p>{errorText(save.error, t('common:error.unexpected'))}</p>
                  {fieldErrors
                    ? Object.values(fieldErrors)
                        .flat()
                        .map((message) => (
                          <p key={message} className="text-xs">
                            {message}
                          </p>
                        ))
                    : null}
                </div>
                {stale && onReload ? (
                  <Button type="button" size="sm" variant="outline" onClick={onReload}>
                    {t('providers.reload')}
                  </Button>
                ) : null}
              </div>
            </Alert>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common:action.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={invalid || unchanged || stale}
              isLoading={save.isPending}
            >
              {route ? t('common:action.save') : t('providers.form.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete a rule ────────────────────────────────────────────────────────

function DeleteDialog({
  route,
  providerName,
  onClose,
}: {
  route: ProviderRoute;
  providerName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const any = t('providers.rules.any');

  const remove = useMutation({
    mutationFn: () => api.deleteProviderRoute(route.id, route.version),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OVERVIEW_KEY });
      onClose();
    },
    // Stale or already gone: either way the table on screen is out of date.
    onError: (error) => {
      if (is409(error) || (error instanceof ZuruflexApiError && error.status === 404)) {
        void queryClient.invalidateQueries({ queryKey: OVERVIEW_KEY });
      }
    },
  });

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('providers.delete.title', { provider: providerName })}</DialogTitle>
          <DialogDescription>
            {t('providers.delete.body', {
              scope: t('providers.delete.scope', {
                country: route.countryCode || any,
                currency: route.currency || any,
              }),
            })}
          </DialogDescription>
        </DialogHeader>
        {remove.error ? (
          <Alert variant={is409(remove.error) ? 'warning' : 'danger'}>
            {errorText(remove.error, t('common:error.unexpected'))}
          </Alert>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant="destructive"
            isLoading={remove.isPending}
            disabled={remove.isError}
            onClick={() => remove.mutate()}
          >
            <Trash2 />
            {t('providers.delete.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Send a test ──────────────────────────────────────────────────────────

/**
 * One real message through exactly one provider. The answer is always a
 * 200 with `ok`, so a refusal is shown as a result, not as an error — only
 * a malformed address comes back as one.
 */
function TestDialog({ provider, onClose }: { provider: ProviderSummary; onClose: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const [to, setTo] = useState('');
  const [result, setResult] = useState<ProviderTestResult | null>(null);
  const isEmail = provider.kind === 'EMAIL';

  const send = useMutation({
    mutationFn: () =>
      api.testProvider({
        kind: provider.kind as ProviderTestKind,
        providerId: provider.id,
        to: to.trim(),
      }),
    onMutate: () => setResult(null),
    onSuccess: setResult,
  });

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('providers.test.title', { provider: provider.displayName })}</DialogTitle>
          <DialogDescription>{t('providers.test.body')}</DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (to.trim()) send.mutate();
          }}
        >
          <FormField
            label={isEmail ? t('providers.test.toEmail') : t('providers.test.toSms')}
            hint={isEmail ? undefined : t('providers.test.toSmsHint')}
          >
            {(field) => (
              <Input
                {...field}
                type={isEmail ? 'email' : 'tel'}
                inputMode={isEmail ? 'email' : 'tel'}
                autoComplete="off"
                placeholder={isEmail ? 'name@example.com' : '+233241234567'}
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            )}
          </FormField>

          {send.error ? (
            <Alert variant="danger">{errorText(send.error, t('common:error.unexpected'))}</Alert>
          ) : null}

          {result ? (
            <div className="space-y-2 rounded-card bg-surface-sunken p-4 text-sm">
              <div className="flex items-center justify-between gap-2">
                <Badge variant={result.ok ? 'success' : 'danger'}>
                  {result.ok
                    ? t('providers.test.ok', { ms: result.durationMs })
                    : t('providers.test.failed', { ms: result.durationMs })}
                </Badge>
                {result.error ? (
                  <span className="font-mono text-xs font-semibold">{result.error.code}</span>
                ) : null}
              </div>
              {result.id ? (
                <p className="text-text-muted">
                  {t('providers.test.id')}:{' '}
                  <span className="break-all font-mono text-xs text-text">{result.id}</span>
                </p>
              ) : null}
              {result.error ? <p className="break-words">{result.error.message}</p> : null}
            </div>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common:action.close')}
            </Button>
            <Button type="submit" disabled={!to.trim()} isLoading={send.isPending}>
              <Send />
              {t('providers.test.send')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
